<script>
	import _ from 'underscore';
	import Q from 'q';
	import assignmentService from 'services/assignmentService';
	const { events } = require('utilities/analyticsUtilities');
	import { studentRateWordsOffline } from 'utilities/offlineUtilities';
	const analyticsUtilities = require('utilities/analyticsUtilities');
	const fireEvent = analyticsUtilities.fireEvent;
	const VOCAB_RATE_THE_WORD = analyticsUtilities.events.VOCAB_RATE_THE_WORD;
	const pubsub = require('events/pubsub');

	function workspaceRatings(ws, post) {
		let wordRatings = {};
		if (ws && ws.scoreState && ws.scoreState.va && ws.scoreState.va.rtw) {
			if (post && ws.scoreState.va.rtw.post) {
				wordRatings = ws.scoreState.va.rtw.post;
			} else if (ws.scoreState.va.rtw.pre) {
				wordRatings = ws.scoreState.va.rtw.pre;
			}
		}
		return wordRatings;
	}

	export default {
		props: {
			product: {
				type: Object,
				required: true
			},
			selectedProduct: {
				type: Object
			},
			workspace: {
				type: Object,
				required: true
			},
			post: {
				type: Boolean,
				default: false
			},
			doneLabel: {
				type: String,
				default: 'Done'
			},
			results: {
				type: Boolean,
				default: false
			},
			showTts: {
				type: Boolean,
				default: false
			},
			printPosition: {
				type: String,
				default: 'before'
			}
		},
		components: {
			'audio-player-simple': require('components/audio-player-simple.vue').default,
			'vocab-rate-words-slider': require('components/vocab-rate-words-slider.vue').default,
			'text-to-speech-player-simple': require('components/text-to-speech-player-simple.vue').default,
			'info-icon': require('components/svg/info-icon.vue').default
		},
		modelData: ['offlineMode', 'printing'],
		data() {
			const wordRatings = this.workspace ? _.assign({}, workspaceRatings(this.workspace, false), workspaceRatings(this.workspace, true)) : {};

			return {
				events,
				submitted: false,
				wordRatings,
				legend: ['Don’t know it', 'Have heard of it but not sure of its meaning', 'Know something about its meaning', 'Know it well']
			};
		},
		computed: {
			isDone() {
				if (this.post || this.results) {
					return false;
				}

				if (!this.post && this.submitted) {
					return true;
				}

				const rtw = workspaceRatings(this.workspace, this.post);
				let foundAll = true;
				this.selectedWords.forEach(w => {
					if (typeof rtw[w] !== 'number') {
						foundAll = false;
					}
				});

				return foundAll;
			},
			selectedWords() {
				const product = this.selectedProduct || this.product;
				const vocab = product.vocab;
				const wordsToMatch = ['word_builder', 'word_matcher', 'word_changer'].reduce((words, activity) => {
					if (this.workspace && !this.workspace.opts.vocabularyActivities.includes(activity)) {
						return words;
					}

					if (vocab && vocab.activity && vocab.activity[activity]) {
						Object.keys(vocab.activity[activity]).forEach(word => words.add(word));
					}

					return words;
				}, new Set());

				const allWords = vocab.support.word_list || [];
				return allWords.filter(word => wordsToMatch.has(word));
			},
			allRated() {
				let allRated = true;
				this.selectedWords.forEach(w => {
					if (typeof this.wordRatings[w] === 'undefined' || this.wordRatings[w] === 0) {
						allRated = false;
					}
				});
				return allRated;
			},
			doneDisabled() {
				let allRated = this.allRated;
				let workspace = this.workspace;
				let results = this.results;

				return !allRated || !workspace || results;
			},
			aid() {
				const ws = this.workspace;

				return ws && ws.aid;
			},
			pid() {
				const ws = this.workspace;

				return ws && ws.productId;
			}
		},
		methods: {
			rateWord(word, rating) {
				this.wordRatings = _.assign({}, this.wordRatings, {
					[word]: rating
				});
			},
			submitRatings() {
				if (!this.post && this.submitted) {
					return;
				}
				const promises = [];
				this.submitted = true;

				const allRatings = {};
				let aid = this.workspace.aid;
				let type = this.post ? 'post' : 'pre';
				let olmRatings = [];

				this.selectedWords.forEach(word => {
					const rating = this.wordRatings[word] || 1;
					allRatings[word] = rating;
					if (aid) {
						let data = {
							aid,
							type,
							word,
							rating: rating
						};
						fireEvent(
							VOCAB_RATE_THE_WORD,
							_.assign({}, data, {
								word: undefined,
								w: word
							})
						);
						if (this.offlineMode) {
							olmRatings.push(data);
						} else {
							promises.push(assignmentService.rateTheWord(data));
						}
					}
				});

				if (this.offlineMode && olmRatings.length) {
					studentRateWordsOffline(aid, olmRatings, type);
				}

				Q.all(promises)
					.then(() => {
						this.$emit('vocab-rtw-submitted');
						pubsub.publish('vocab-rtw-submitted', {
							[this.post ? 'post' : 'pre']: allRatings
						});
					})
					.catch(() => {
						this.submitted = false;
					});
			}
		}
	};
</script>
<template>
	<div
		v-if="printing"
		class="vocab-rate-words-container printing">
		<div v-if="printPosition === 'before'">
			<div class="print-header">1. Before you start reading...</div>
			<p class="print-subheader">Here are the vocabulary words that will be in this reading. Let’s see how well you already know them.</p>
			<p>Check the box that shows how well you know each word. It’s ok if you don’t know them yet (this is not graded)!</p>
		</div>
		<div v-else>
			<div class="print-header">3. After reading and exploring the words through some activities...</div>
			<p class="print-subheader">
				Do you know these words better? Check the box that shows how well you know each word. It’s ok if you don’t know them yet (this is not graded)!
			</p>
		</div>
		<table class="word-table">
			<thead>
				<tr>
					<th scope="col"></th>
					<th
						v-for="legendText in legend"
						:key="legendText"
						scope="col"
						v-text="legendText"></th>
				</tr>
			</thead>
			<tbody>
				<tr
					v-for="word in selectedWords"
					:key="word">
					<th scope="row">{{ word }}</th>
					<td
						v-for="l in legend"
						:key="l"></td>
				</tr>
			</tbody>
		</table>
	</div>
	<div
		v-else
		class="vocab-rate-words-container">
		<div
			class="vocab-rate-words"
			v-if="!isDone">
			<template v-if="post || results">
				<h1>
					After reading and exploring the words through some activities…
					<text-to-speech-player-simple
						v-if="showTts"
						text="After reading and exploring the words through some activities…"
						:event-data="{
							aid,
							pid,
							tag: 'rtw_after_h1'
						}">
					</text-to-speech-player-simple>
				</h1>
				<p>
					Do you know these words better? Move the dots to show how well you know the words. Once again, it’s ok if you don’t know them yet (this is not
					graded)!
					<text-to-speech-player-simple
						v-if="showTts"
						text="Do you know these words better? Move the dots to show how well you know the words. Once again, it’s ok if you don’t know them yet (this is not graded)!"
						:event-data="{
							aid,
							pid,
							tag: 'rtw_after_instructions'
						}">
					</text-to-speech-player-simple>
				</p>
			</template>
			<template v-else>
				<h1>
					Before you start reading…
					<text-to-speech-player-simple
						v-if="showTts"
						text="Before you start reading…"
						:event-data="{
							aid,
							pid,
							tag: 'rtw_before_h1'
						}">
					</text-to-speech-player-simple>
				</h1>
				<p>Here are the vocabulary words that will be in this reading. Let’s see how well you already know them.</p>
				<p>
					Move the dots to show how well you know each word. It’s ok if you don’t know them yet (this is not graded)!
					<text-to-speech-player-simple
						v-if="showTts"
						text="Here are the vocabulary words that will be in this reading. Let’s see how well you already know them. Move the dots to show how well you know each word. It’s ok if you don’t know them yet (this is not graded)!"
						:event-data="{
							aid,
							pid,
							tag: 'rtw_before_instructions'
						}">
					</text-to-speech-player-simple>
				</p>
			</template>
			<div>
				<div
					class="rated-word"
					v-for="word in selectedWords"
					:key="word">
					<span>
						{{ word }}
						<audio-player-simple
							v-if="showTts"
							:url="product.wordAudioUrl(word)"
							:event-data="{ aid, pid, word }"
							:audio-play-event-name="events.WORDSMYTH_AUDIO_PLAY"
							:audio-pause-event-name="events.WORDSMYTH_AUDIO_PAUSE"
							:audio-complete-event-name="events.WORDSMYTH_AUDIO_COMPLETE">
						</audio-player-simple>
					</span>
					<vocab-rate-words-slider
						:readonly="results"
						:value="wordRatings[word]"
						@update-value="
							value => {
								rateWord(word, value);
							}
						"></vocab-rate-words-slider>
				</div>
			</div>
			<div class="rated-word-legend">
				<div
					v-for="legendText in legend"
					:key="legendText">
					{{ legendText }}
					<text-to-speech-player-simple
						v-if="showTts"
						:text="legendText"
						:event-data="{
							aid,
							pid,
							tag: 'rtw_legend'
						}">
					</text-to-speech-player-simple>
				</div>
			</div>
		</div>
		<div
			class="rate-words-done-container"
			v-if="!isDone">
			<button
				v-if="doneDisabled"
				class="button -link tooltip-top"
				v-tooltip="'Complete the activity to move on to the passage!'">
				<info-icon></info-icon>
			</button>
			<button
				type="button"
				class="button -large rate-words-done"
				:disabled="doneDisabled"
				@click="submitRatings">
				{{ doneLabel }}
			</button>
		</div>
	</div>
</template>
<style lang="scss">
	@import 'config/colors';
	@import 'config/variables';

	.vocab-rate-words {
		margin: 1rem;
		font-family: var(--custom-font-family, $helvetica);
		background-color: white;
		border: 1px solid $c-slate;
		padding: 1rem;
		$slider-width: 80%;
		$word-width: 18%;

		h1 {
			font-size: 1.4rem;
			font-weight: 200;
			margin: 0.5rem 0;
		}

		.rated-word {
			margin-bottom: 1.5rem;

			> span {
				font-weight: bold;
				display: block;
			}
		}

		.rated-word:nth-child(1) {
			margin-top: 1.7rem;
		}

		@media only screen and (min-width: $bp-width-med) {
			padding: 2rem 8rem;

			h1 {
				font-size: 1.5rem;
			}

			.closer {
				font-size: 4rem;
				top: 0.5rem;
				right: 0.5rem;
			}

			.rated-word {
				> span {
					display: inline-block;
					width: $word-width;
				}

				.vocab-rate-words-slider {
					float: right;
					margin-top: 0.3rem;
					width: $slider-width;
				}
			}
		}

		.rated-word-legend {
			display: flex;
			flex-wrap: wrap;

			> div {
				border-top: 1rem solid transparent;
				padding-top: 0.6rem;
				width: 46%;
				margin: 0 2%;
				text-align: center;
			}

			> div:nth-child(1) {
				border-color: $c-rate-words-slider-color1;
			}

			> div:nth-child(2) {
				border-color: $c-rate-words-slider-color2;
			}

			> div:nth-child(3) {
				border-color: $c-rate-words-slider-color3;
			}

			> div:nth-child(4) {
				border-color: $c-rate-words-slider-color4;
			}
		}

		@media only screen and (min-width: $bp-width-med) {
			.rated-word-legend {
				flex-wrap: nowrap;
				padding-left: 100% - $slider-width;

				> div {
					width: 21%;
					margin: 0 2%;
				}
			}
		}
	}

	.vocab-rate-words-container {
		.rate-words-done-container {
			text-align: right;

			.button.rate-words-done {
				margin: 0.5rem 1rem;
			}
		}

		&.printing {
			.print-header {
				font-weight: 700;
			}

			.print-subheader {
				margin-top: 0.5rem;
			}

			.word-table {
				width: 100%;
				table-layout: fixed;
				border-collapse: collapse;

				th,
				td {
					border: 1px solid #000;
					font-size: 0.875rem;
					padding: 0 0.25rem 0.5rem;
				}

				thead {
					background-color: none;
					border: none;

					th {
						font-weight: 400;
						text-transform: none;
						text-align: center;

						&:first-child {
							border: none;
						}
					}
				}

				tbody {
					th {
						text-align: left;
					}
				}
			}
		}
	}
</style>
